import {useCallback, useState} from "react";
import {useMutation} from "@apollo/client";
import {useKeycloak} from "@react-keycloak/web";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import {UpdateInstalledBaseNode} from "../graphql/mutations/updateInstalledBaseNode";
import {NODE_IMAGE_REFETCH_QUERIES} from "../utils/nodeImage";
import {uploadSingleFile} from "../utils/uploadSingleFile";
import {resolveErrorMessage} from "../utils/resolveErrorMessage";
import {isImageFile} from "../utils/fileType";

/**
 * Everything needed to set or unlink the image of one node outside the TDA
 * form: the update input requires `key` and `title`, `currentImageId` is the
 * node-file id of the current image (`installedBaseNode.image.id`) and
 * `canWrite` mirrors `installedBaseNode.access.write`.
 */
export interface NodeImageTarget {
    nodeId: string;
    key: string;
    title: string;
    currentImageId: string | null;
    canWrite: boolean;
    /** Translated node type label for sentence use ("asset", "system", …),
     *  e.g. for "Use as {{nodeType}} image". Optional — callers without it
     *  fall back to the generic wording. */
    nodeTypeLabel?: string;
}

/**
 * Builds a target from a node selected with `id key title access { write } image { id }`.
 * Pass `nodeTypeLabel` (see nodeTypeLabelForSentence) where the UI names the type.
 */
export const buildNodeImageTarget = (node: any, nodeTypeLabel?: string): NodeImageTarget | null => {
    if (!node?.id || !node?.key || !node?.title) return null;
    return {
        nodeId: String(node.id),
        key: node.key,
        title: node.title,
        currentImageId: node.image?.id ?? null,
        canWrite: !!node.access?.write,
        nodeTypeLabel: nodeTypeLabel || undefined,
    };
};

/**
 * Node type name as it reads mid-sentence: English lower-cases ("Use as asset
 * image"), German keeps the noun capitalised ("Als Asset-Bild verwenden").
 */
export const nodeTypeLabelForSentence = (label: string | null | undefined, language: string | undefined): string | undefined => {
    if (!label) return undefined;
    return language?.toLowerCase().startsWith('de') ? label : label.toLowerCase();
};

/**
 * Sets, replaces or unlinks a node image with immediate feedback (toasts) and
 * refetches every active query that renders it. `fileId` is the file-service
 * UUID; `null` unlinks the image and keeps the file.
 */
export const useNodeImage = () => {
    const {t} = useTranslation();
    const {keycloak} = useKeycloak();
    const [updateNode] = useMutation(UpdateInstalledBaseNode);
    const [busy, setBusy] = useState(false);

    const setNodeImage = useCallback(async (target: NodeImageTarget, fileId: string | null): Promise<boolean> => {
        setBusy(true);
        try {
            await updateNode({
                variables: {
                    id: target.nodeId,
                    input: {key: target.key, title: target.title, imageId: fileId},
                },
                refetchQueries: NODE_IMAGE_REFETCH_QUERIES,
            });
            toast.success(fileId ? t('Image updated.') : t('Image removed.'));
            return true;
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
            return false;
        } finally {
            setBusy(false);
        }
    }, [updateNode, t]);

    const uploadAndSetNodeImage = useCallback(async (target: NodeImageTarget, file: File): Promise<boolean> => {
        if (!isImageFile(file.type)) {
            toast.error(t('Only image files are allowed.'));
            return false;
        }
        setBusy(true);
        let fileId: string;
        try {
            fileId = await uploadSingleFile(file, keycloak.token);
        } catch (err) {
            toast.error(t('Image upload failed.'));
            setBusy(false);
            return false;
        }
        return setNodeImage(target, fileId);
    }, [keycloak.token, setNodeImage, t]);

    return {setNodeImage, uploadAndSetNodeImage, busy};
};
